import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import { Upload, Camera, AlertTriangle, CheckCircle, Shield, FileText, Zap, PieChart, BarChart3, Mic, Cpu, Eye, Sparkles } from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import VoiceNoteRecorder from './VoiceNoteRecorderV2';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Professional header image - AI safety monitoring
const HEADER_IMAGE_URL = "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=1600&q=80";

const COLORS = {
  critical_risk: '#DC2626',
  imminent_danger: '#991B1B',
  high_risk: '#EA580C',
  moderate_risk: '#F59E0B',
  low_risk: '#10B981',
  informational: '#3B82F6'
};

const SafetyAnalysisPageV2 = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/safety/analyses`);
      setAnalyses(response.data || []);
    } catch (error) {
      console.error('Error loading analyses:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File",
        description: "Please select an image file (PNG, JPG, JPEG)",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select an image smaller than 10MB",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleAddVoiceNote = (voiceNote) => {
    setVoiceNotes(prev => [...prev, voiceNote]);
    toast({
      title: "Voice Note Added",
      description: "Voice note transcribed and saved successfully"
    });
  };

  const analyzeImage = async () => {
    if (!selectedFile) {
      toast({
        title: "No Image Selected",
        description: "Please select an image first",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // Include voice notes if any
      if (voiceNotes.length > 0) {
        formData.append('voice_notes', JSON.stringify(voiceNotes));
      }

      const response = await axios.post(`${API}/safety/analyze-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setAnalysisResults(response.data);
      toast({
        title: "Analysis Complete",
        description: response.data.message || "Image analysis completed successfully!"
      });
      
      fetchAnalyses();
    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: error.response?.data?.detail || "Image analysis failed. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical_risk': 'bg-red-100 text-red-800',
      'imminent_danger': 'bg-red-200 text-red-900',
      'high_risk': 'bg-orange-100 text-orange-800',
      'moderate_risk': 'bg-yellow-100 text-yellow-800',
      'low_risk': 'bg-green-100 text-green-800',
      'informational': 'bg-blue-100 text-blue-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getRiskScoreColor = (score) => {
    if (score >= 0.8) return 'text-red-600';
    if (score >= 0.6) return 'text-orange-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Prepare data for charts
  const prepareChartData = () => {
    if (!analysisResults || !analysisResults.detailed_violations) return null;

    // Severity Distribution
    const severityCount = {};
    analysisResults.detailed_violations.forEach(v => {
      const severity = v.severity || 'unknown';
      severityCount[severity] = (severityCount[severity] || 0) + 1;
    });

    const severityData = Object.entries(severityCount).map(([severity, count]) => ({
      name: severity.replace(/_/g, ' ').toUpperCase(),
      value: count,
      color: COLORS[severity] || '#6B7280'
    }));

    // Violation Categories
    const categoryCount = {};
    analysisResults.detailed_violations.forEach(v => {
      const type = v.type?.split(' - ')[0] || 'Other';
      categoryCount[type] = (categoryCount[type] || 0) + 1;
    });

    const categoryData = Object.entries(categoryCount).map(([category, count]) => ({
      category,
      count
    }));

    // PPE Analysis Data
    const ppeData = analysisResults.ppe_analysis ? 
      Object.entries(analysisResults.ppe_analysis).map(([item, data]) => ({
        item: item.replace(/_/g, ' ').toUpperCase(),
        compliance: data.compliance ? 100 : 0,
        required: data.required ? 100 : 0
      })) : [];

    // Risk Assessment Radar
    const radarData = [
      { category: 'PPE Compliance', score: analysisResults.ppe_analysis ? 
        (Object.values(analysisResults.ppe_analysis).filter(p => p.compliance).length / 
         Object.values(analysisResults.ppe_analysis).length * 100) : 50 },
      { category: 'Equipment Safety', score: analysisResults.equipment_analysis ? 
        (Object.values(analysisResults.equipment_analysis).filter(e => e.compliant).length / 
         Object.values(analysisResults.equipment_analysis).length * 100) : 50 },
      { category: 'Overall Compliance', score: analysisResults.compliance_percentage || 50 },
      { category: 'Hazard Level', score: 100 - (analysisResults.overall_risk_score * 100) },
      { category: 'Critical Issues', score: 100 - ((analysisResults.critical_violations || 0) / 
        (analysisResults.violations_detected || 1) * 100) }
    ];

    return { severityData, categoryData, ppeData, radarData };
  };

  const chartData = analysisResults ? prepareChartData() : null;

  return (
    <div className="space-y-6">
      {/* Stylized Header with Visual */}
      <div className="relative rounded-2xl overflow-hidden shadow-xl">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${HEADER_IMAGE_URL})`,
          }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-indigo-900/90 to-purple-900/85" />
        
        {/* Content */}
        <div className="relative px-8 py-12">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <Cpu className="w-7 h-7 text-cyan-400" />
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 px-3 py-1">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI-Powered
                  </Badge>
                  <Badge className="bg-green-500/20 text-green-300 border-green-500/30 px-3 py-1">
                    <Eye className="w-3 h-3 mr-1" />
                    Visual Analysis
                  </Badge>
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                AI Safety Analysis
              </h1>
              <p className="text-lg text-blue-100/90 max-w-2xl">
                Upload workplace images for comprehensive OSHA compliance analysis with AI-powered visual risk assessment and detailed violation detection
              </p>
            </div>
            
            {/* Stats Cards */}
            <div className="hidden lg:flex gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                <Shield className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{analyses.length}</p>
                <p className="text-xs text-blue-200">Analyses</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                <AlertTriangle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {analyses.reduce((acc, a) => acc + (a.violation_count || 0), 0)}
                </p>
                <p className="text-xs text-blue-200">Violations Found</p>
              </div>
            </div>
          </div>
          
          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2 mt-6">
            {[
              { icon: Camera, text: 'Image Analysis' },
              { icon: AlertTriangle, text: 'Hazard Detection' },
              { icon: Shield, text: 'PPE Compliance' },
              { icon: FileText, text: 'OSHA Standards' },
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                <feature.icon className="w-4 h-4 text-blue-300" />
                <span className="text-sm text-white/90">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Camera className="w-5 h-5 mr-2 text-blue-600" />
            Upload Safety Image
          </CardTitle>
          <CardDescription>
            Upload photos of workplace conditions for AI analysis with detailed visual breakdowns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {previewUrl ? (
              <div className="space-y-4">
                <img src={previewUrl} alt="Preview" className="max-h-64 mx-auto rounded-lg shadow-lg" />
                
                {/* Voice Notes Section */}
                {voiceNotes.length > 0 && (
                  <div className="text-left">
                    <p className="text-sm font-medium mb-2">Voice Notes ({voiceNotes.length})</p>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {voiceNotes.map((note, idx) => (
                        <div key={note.id} className="text-xs bg-gray-100 p-2 rounded">
                          {note.transcription.substring(0, 100)}...
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 justify-center">
                  <Button onClick={analyzeImage} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Analyze Image
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowVoiceRecorder(true)}
                    disabled={loading}
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Add Voice Note
                  </Button>
                  <Button variant="outline" onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl(null);
                    setAnalysisResults(null);
                    setVoiceNotes([]);
                  }}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Click to upload or drag and drop
                </p>
                <p className="text-sm text-gray-500">
                  PNG, JPG, JPEG up to 10MB
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Analysis Results with Visual Charts */}
      {analysisResults && chartData && (
        <>
          {/* Summary Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-blue-600" />
                  Safety Analysis Summary
                </span>
                <Badge className={getRiskScoreColor(analysisResults.overall_risk_score)}>
                  Risk Score: {(analysisResults.overall_risk_score * 100).toFixed(0)}%
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">Compliance Rate</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {analysisResults.compliance_percentage?.toFixed(1)}%
                  </p>
                  <Progress value={analysisResults.compliance_percentage} className="mt-2" />
                </div>
                
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-orange-600 font-medium">Total Violations</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {analysisResults.violations_detected || 0}
                  </p>
                </div>
                
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-red-600 font-medium">Critical Issues</p>
                  <p className="text-2xl font-bold text-red-900">
                    {analysisResults.critical_violations || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Visual Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Severity Distribution Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <PieChart className="w-5 h-5 mr-2 text-blue-600" />
                  Violation Severity Distribution
                </CardTitle>
                <CardDescription>Breakdown of safety issues by severity level</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={chartData.severityData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.severityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {chartData.severityData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: item.color }}></div>
                        <span>{item.name}</span>
                      </div>
                      <span className="font-semibold">{item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Violation Categories Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-orange-600" />
                  Violation Categories
                </CardTitle>
                <CardDescription>Types of safety violations identified</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.categoryData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#F59E0B" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* PPE Compliance Chart */}
            {chartData.ppeData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-green-600" />
                    PPE Compliance Status
                  </CardTitle>
                  <CardDescription>Personal protective equipment analysis</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData.ppeData} layout="horizontal">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis dataKey="item" type="category" width={120} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="compliance" fill="#10B981" name="Compliant" />
                      <Bar dataKey="required" fill="#EF4444" name="Required" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Risk Assessment Radar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-purple-600" />
                  Multi-Factor Risk Assessment
                </CardTitle>
                <CardDescription>Comprehensive safety metrics analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={chartData.radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="category" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar name="Safety Score" dataKey="score" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Violations List */}
          {analysisResults.detailed_violations && analysisResults.detailed_violations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-red-600" />
                  Detailed Violations & Corrective Actions
                </CardTitle>
                <CardDescription>Comprehensive list of identified safety issues</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analysisResults.detailed_violations.map((violation, index) => (
                    <Card key={index} className="border-l-4" style={{ borderLeftColor: COLORS[violation.severity] || '#6B7280' }}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center">
                            <AlertTriangle className="w-5 h-5 mr-2" style={{ color: COLORS[violation.severity] }} />
                            <h4 className="font-semibold text-gray-900">{violation.type}</h4>
                          </div>
                          <Badge className={getSeverityColor(violation.severity)}>
                            {violation.severity.replace('_', ' ')}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-2">{violation.location}</p>
                        
                        <div className="flex items-center text-sm text-blue-600 mb-2">
                          <FileText className="w-4 h-4 mr-1" />
                          {violation.osha_reference}
                        </div>
                        
                        {violation.immediate_action && (
                          <Badge variant="destructive" className="mb-2">
                            Immediate Action Required
                          </Badge>
                        )}
                        
                        {violation.corrective_actions && violation.corrective_actions.length > 0 && (
                          <div className="mt-3 bg-green-50 p-3 rounded">
                            <p className="text-sm font-medium text-green-800 mb-1">Corrective Actions:</p>
                            <ul className="text-sm text-green-700 list-disc list-inside space-y-1">
                              {violation.corrective_actions.map((action, i) => (
                                <li key={i}>{action}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Previous Analyses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            Previous Analyses
          </CardTitle>
          <CardDescription>
            {analyses.length === 0 ? 'No analyses found. Upload an image to get started!' : `${analyses.length} recent analyses`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {analyses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p>Upload your first workplace image for AI safety analysis</p>
            </div>
          ) : (
            <div className="space-y-3">
              {analyses.slice(0, 10).map((analysis, index) => (
                <div 
                  key={analysis.id || index} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => navigate(`/safety-analysis/${analysis.id}`)}
                >
                  <div className="flex items-center space-x-3">
                    {analysis.image_thumbnail ? (
                      <img 
                        src={analysis.image_thumbnail} 
                        alt={analysis.file_name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <Camera className="w-16 h-16 text-gray-400 p-2" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{analysis.file_name || 'Analysis'}</p>
                      <p className="text-sm text-gray-500">
                        {analysis.violations_detected || analysis.detected_violations?.length || 0} violations • 
                        {analysis.compliance_percentage ? ` ${analysis.compliance_percentage.toFixed(1)}% compliant` : ' Analyzed'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {new Date(analysis.analysis_timestamp).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Badge className={getRiskScoreColor(analysis.overall_risk_score || 0.5)}>
                    {analysis.overall_risk_score ? `${(analysis.overall_risk_score * 100).toFixed(0)}% risk` : 'Analyzed'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Voice Note Recorder Modal */}
      <VoiceNoteRecorder
        isOpen={showVoiceRecorder}
        onSave={handleAddVoiceNote}
        existingNotes={voiceNotes}
        onClose={() => setShowVoiceRecorder(false)}
      />
    </div>
  );
};

export default SafetyAnalysisPageV2;
